import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'forgot'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    country_code: '+91'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);
  const [accountRejected, setAccountRejected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingSession, setProcessingSession] = useState(false);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');
    if (errorParam === 'pending_approval') {
      setPendingApproval(true);
      setError('');
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }
    if (errorParam === 'account_rejected') {
      setAccountRejected(true);
      setError('');
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }
    if (errorParam) {
      setError(messageParam || 'Google authentication failed');
      window.history.replaceState(null, '', window.location.pathname);
    }
    
    // Check for existing session
    checkExistingSession();
    
    // Check for session_id in URL fragment (from legacy Emergent Auth)
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleLegacyGoogleCallback(sessionId);
    }
  }, [searchParams]);

  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/session`, {
        withCredentials: true,
        validateStatus: (status) => status < 500 // Don't throw for 401/403
      });
      
      if (response.status === 200 && response.data) {
        // Already authenticated, redirect to dashboard
        navigate('/dashboard');
      }
      // 401 means not authenticated - this is expected, stay on auth page
    } catch (error) {
      // Network error or server error - stay on auth page
      console.log('Session check failed:', error.message);
    }
  };

  // Legacy callback handler for Emergent Auth (kept for backward compatibility)
  const handleLegacyGoogleCallback = async (sessionId) => {
    setProcessingSession(true);
    setError('');

    try {
      // Call backend to process Google session (legacy Emergent Auth)
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/google/callback`,
        { session_id: sessionId },
        { withCredentials: true }
      );

      if (response.data.success) {
        // Clean URL fragment
        window.history.replaceState(null, '', window.location.pathname);
        
        // Redirect to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Google authentication failed');
      setProcessingSession(false);
      // Clean URL fragment
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to backend Google OAuth endpoint - uses dynamic origin for redirect
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/signup`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        company_name: formData.company_name,
        phone: formData.phone,
        country_code: formData.country_code
      });

      if (response.data.success) {
        setFormData({ email: '', password: '', name: '', company_name: '', phone: '', country_code: '+91' });
        
        setSuccess(
          <div>
            <p className="font-semibold mb-2">Account created successfully!</p>
            <p className="text-sm">A verification email has been sent to <strong>{formData.email}</strong></p>
            <p className="text-xs mt-2 text-gray-600">Please check your inbox (and spam folder) to verify your email.</p>
            <p className="text-xs mt-1 text-orange-600">After verification, your account will be reviewed. Approval may take up to 2 hours.</p>
          </div>
        );
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        {
          email: formData.email,
          password: formData.password
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      if (detail.startsWith('PENDING_APPROVAL:')) {
        setPendingApproval(true);
        setError('');
      } else if (detail.startsWith('ACCOUNT_REJECTED:')) {
        setAccountRejected(true);
        setError('');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: formData.email
      });

      if (response.data.success) {
        // For development, show reset link
        if (response.data.reset_token) {
          const resetUrl = `${window.location.origin}/reset-password?token=${response.data.reset_token}`;
          console.log('Reset URL:', resetUrl);
          setSuccess(
            <div>
              <p className="font-semibold mb-2">✅ Password reset link generated!</p>
              <p className="text-sm mb-2">Click the link below to reset your password:</p>
              <a 
                href={resetUrl}
                className="text-blue-600 hover:text-blue-800 underline text-sm break-all"
                target="_blank"
                rel="noopener noreferrer"
              >
                {resetUrl}
              </a>
              <p className="text-xs mt-2 text-gray-600">Or copy and paste this link into your browser</p>
            </div>
          );
        } else {
          setSuccess('Password reset link has been sent to your email');
        }
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (processingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Processing authentication...</p>
        </div>
      </div>
    );
  }

  if (pendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-400 to-amber-500 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center" data-testid="pending-approval-screen">
          <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Account Pending Approval</h1>
          <p className="text-gray-600 mb-4">
            Your account has been created and is pending approval from the Admin.
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-orange-800 font-medium">Approval may take up to 2 hours</p>
            <p className="text-xs text-orange-600 mt-1">You will receive an email once your account is approved.</p>
          </div>
          <button
            onClick={() => { setPendingApproval(false); setMode('login'); }}
            className="text-sm text-purple-600 hover:text-purple-800 underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (accountRejected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-500 to-gray-700 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center" data-testid="account-rejected-screen">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Account Not Approved</h1>
          <p className="text-gray-600 mb-4">
            Your account application was not approved at this time.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">If you believe this was in error or would like to re-apply, please contact our support team.</p>
            <a href="mailto:support@loopai.in" className="inline-block mt-3 text-sm font-medium text-purple-600 hover:text-purple-800 underline">
              support@loopai.in
            </a>
          </div>
          <button
            onClick={() => { setAccountRejected(false); setMode('login'); }}
            className="text-sm text-purple-600 hover:text-purple-800 underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">LoopAi Creator</h1>
          <p className="text-gray-600">
            {mode === 'signup' && 'Create your account'}
            {mode === 'login' && 'Welcome back'}
            {mode === 'forgot' && 'Reset your password'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Min 6 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company / Brand Name</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Your company or brand name"
                data-testid="signup-company-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <div className="flex gap-2">
                <select
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleInputChange}
                  className="w-24 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  data-testid="signup-country-code"
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                  <option value="+971">+971</option>
                  <option value="+65">+65</option>
                  <option value="+81">+81</option>
                  <option value="+49">+49</option>
                  <option value="+33">+33</option>
                  <option value="+86">+86</option>
                  <option value="+82">+82</option>
                  <option value="+55">+55</option>
                  <option value="+7">+7</option>
                  <option value="+234">+234</option>
                  <option value="+254">+254</option>
                  <option value="+27">+27</option>
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Phone number"
                  data-testid="signup-phone"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 disabled:opacity-50 font-medium"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>
        )}

        {mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <div className="text-right">
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="text-sm text-purple-500 hover:text-purple-600"
              >
                Forgot password?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 disabled:opacity-50 font-medium"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 disabled:opacity-50 font-medium"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="mt-4 w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 font-medium"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google
          </button>
        </div>

        <div className="mt-6 text-center text-sm">
          {mode === 'login' && (
            <>
              Don't have an account?{' '}
              <button
                onClick={() => {
                  setMode('signup');
                  setError('');
                  setSuccess('');
                }}
                className="text-purple-500 hover:text-purple-600 font-medium"
              >
                Sign up
              </button>
            </>
          )}
          {(mode === 'signup' || mode === 'forgot') && (
            <>
              Already have an account?{' '}
              <button
                onClick={() => {
                  setMode('login');
                  setError('');
                  setSuccess('');
                }}
                className="text-purple-500 hover:text-purple-600 font-medium"
              >
                Log in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Auth;
